import { useTranslation } from 'react-i18next'

import Icon from '../../../ui/Icon'
import { Button } from '../../../buttons/Button'

import { PassColor, PassProduct } from '../../../../types/booking/booking'
import { DayOfWeekOrder, formatAvailableDay, haveSameDays } from '../../../../utils/dateUtils'
import { formatMinutes, formatMoney } from '../../../../utils/booking/booking'

type PassProductCardProps = {
  product: PassProduct
  actionLabel: string
  onAction: (product: PassProduct) => void
}

const colorClass: Record<PassColor, string> = {
  [PassColor.DEFAULT]: 'default',
  [PassColor.DARK]: 'dark',
  [PassColor.ACCENT]: 'accent',
}

/** A prepaid pass on sale: where it can be used, how many minutes, for how long, and on which days, hours and courts. */
export const PassProductCard = ({ product, actionLabel, onAction }: PassProductCardProps) => {
  const { t } = useTranslation(['passes', 'common'])
  const color = product.color ?? PassColor.DEFAULT

  const days = haveSameDays(product.weekdays, DayOfWeekOrder)
    ? t('passes.card.every-day')
    : haveSameDays(product.weekdays, DayOfWeekOrder.slice(0, 5))
      ? t('passes.card.weekdays')
      : haveSameDays(product.weekdays, DayOfWeekOrder.slice(5))
        ? t('passes.card.weekend')
        : DayOfWeekOrder.filter(day => product.weekdays.includes(day))
            .map(day => formatAvailableDay(day, t))
            .join(', ')

  const courts = product.courtNames ?? []

  return (
    <div className={`plan-card ${colorClass[color]}`}>
      <div className="ph-row ph-between align-items-start">
        <h3 className="mt-1">{product.name}</h3>
        {product.mostPopular && <span className="badge badge-accent">{t('passes.card.most-popular')}</span>}
      </div>

      <div>
        <div className="price">{formatMoney(product.price, product.currency)}</div>
        <div className="per">{t('passes.card.validity-days', { days: product.validityDays })}</div>
      </div>

      <ul className="plan-card-terms">
        {product.clubName && (
          <li>
            <Icon name="pin" size={15} />
            <div>
              <span className="plan-card-term-label">{t('passes.card.club')}</span>
              <span>{product.clubName}</span>
            </div>
          </li>
        )}
        <li>
          <Icon name="ball" size={15} />
          <div>
            <span className="plan-card-term-label">{t('passes.card.play-time')}</span>
            <span>{t('passes.card.minutes-included', { minutes: formatMinutes(product.includedMinutes) })}</span>
          </div>
        </li>
        <li>
          <Icon name="calendar" size={15} />
          <div>
            <span className="plan-card-term-label">{t('passes.card.days')}</span>
            <span>{days}</span>
          </div>
        </li>
        {Boolean(product.bookAheadDays) && (
          <li>
            <Icon name="flag" size={15} />
            <div>
              <span className="plan-card-term-label">{t('passes.card.book-ahead')}</span>
              <span>{t('passes.card.book-ahead-days', { days: product.bookAheadDays })}</span>
              <span className="plan-card-term-hint">{t('passes.card.book-ahead-hint')}</span>
            </div>
          </li>
        )}
        <li>
          <Icon name="clock" size={15} />
          <div>
            <span className="plan-card-term-label">{t('passes.card.hours')}</span>
            {product.intervals.map(interval => (
              <span key={`${interval.startTime}-${interval.endTime}`} className="mono">
                {interval.startTime.slice(0, 5)} – {interval.endTime.slice(0, 5)}
              </span>
            ))}
            <span className="plan-card-term-hint">{t('passes.card.hours-hint')}</span>
          </div>
        </li>
        {courts.length > 0 && (
          <li>
            <Icon name="court" size={15} />
            <div>
              <span className="plan-card-term-label">
                {t(courts.length === 1 ? 'passes.card.court' : 'passes.card.courts')}
              </span>
              <span>{courts.join(', ')}</span>
            </div>
          </li>
        )}
      </ul>

      <Button
        label={actionLabel}
        variant={color === PassColor.ACCENT ? 'dark' : 'primary'}
        block
        onClick={() => onAction(product)}
      />
    </div>
  )
}
